import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import { Globe, ArrowRight, CheckCircle, Shield, Mic, Send, Zap, DollarSign, Clock } from 'lucide-react';

export default function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 pb-20 md:pb-0">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Welcome back, {user?.name || user?.full_name || 'User'}!
            </h1>
            <p className="text-lg text-gray-600">
              Your RouteFlow AI Dashboard
            </p>
          </div>

          {/* Primary Action - Send Money */}
          <Card 
            className="cursor-pointer hover:shadow-xl transition-all border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-teal-50 mb-6"
            onClick={() => navigate('/send-money')}
          >
            <CardContent className="pt-6 pb-6">
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 rounded-full bg-emerald-600 flex items-center justify-center shadow-lg">
                  <Send className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-emerald-900">Send Money</h2>
                  <p className="text-emerald-700">Fast, low-cost international transfers</p>
                  <div className="flex gap-4 mt-2 text-sm text-emerald-600">
                    <span className="flex items-center gap-1"><Zap className="w-4 h-4" /> Minutes, not days</span>
                    <span className="flex items-center gap-1"><DollarSign className="w-4 h-4" /> Only 0.48% fee</span>
                  </div>
                </div>
                <ArrowRight className="w-8 h-8 text-emerald-600" />
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all border-2 hover:border-emerald-300"
              onClick={() => navigate('/voice-assistant')}
            >
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <Mic className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Voice Assistant</h3>
                    <p className="text-sm text-gray-500">Send money using voice commands</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 ml-auto" />
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all border-2 hover:border-blue-300"
              onClick={() => navigate('/kyc')}
            >
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Account Verification</h3>
                    <p className="text-sm text-gray-500">Unlock higher transfer limits</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 ml-auto" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Features Section */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-xl">Why RouteFlow AI?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">44 Fiat Currencies</h3>
                    <p className="text-sm text-gray-600">USD, EUR, GBP, CAD, AED, and many more</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">121 Countries</h3>
                    <p className="text-sm text-gray-600">Send money almost anywhere in the world</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Blockchain Speed</h3>
                    <p className="text-sm text-gray-600">Transfers settle in minutes using crypto rails</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">AI-Powered</h3>
                    <p className="text-sm text-gray-600">Smart routing finds the best rates automatically</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity Placeholder */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <Clock className="w-5 h-5 text-gray-500" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-gray-500">
                <Globe className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No transfers yet</p>
                <p className="text-sm mt-1">Your transaction history will appear here</p>
                <Button 
                  className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => navigate('/send-money')}
                >
                  Make Your First Transfer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
